import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import { injectOpenJobEditor } from '@core/offers-logic';
import { JobEditorDialogTab } from '@core/offers-types';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class PublishJobWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);
  private readonly openJobEditor = injectOpenJobEditor();

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.PublishJob;

  private steps: Step[];

  constructor() {
    this.buildSteps();
  }

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(
          state,
          this.subtask,
          ['offers'],
          false,
          true,
        );
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  public navigateToNextStep(step: Step['id'] | null): void {
    const notPublishedId = 'draft';
    const publishedId = 'published';

    switch (step) {
      case 'walkthrough-step-publish-job-1':
        this.openJobEditor(notPublishedId, JobEditorDialogTab.Application);
        break;

      case 'walkthrough-step-publish-job-4':
        this.router.navigate([{ outlets: { 'job-editor': null } }]).then(() => {
          this.router.navigate(['offers', publishedId, 'promote']);
        });
        break;

      case 'walkthrough-step-publish-job-5':
        this.openJobEditor(publishedId, JobEditorDialogTab.JobDetails);
        break;

      default:
        this.router.navigate(['offers']);
        break;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private buildSteps(): void {
    this.steps = [
      {
        id: 'walkthrough-step-job-1',
        position: 1,
        component: {
          definition: WalkthroughDialogComponent,
          inputs: {
            heading: this.i18n.translate('app.walkthrough.walkthroughs.publish_job.step_1.heading'),
            description: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_1.description',
            ),
            imageUrl: walkthroughAssetsResolver('publish_job.svg'),
            ctaText: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_1.cta_text',
            ),
          },
        },
        renderOptions: {
          strategy: globalPositionStrategyFactory,
        },
      },
      {
        id: 'walkthrough-step-publish-job-2',
        position: 2,
        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['middle', 'right'],
            heading: this.i18n.translate('app.walkthrough.walkthroughs.publish_job.step_2.heading'),
            description: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_2.description',
            ),
            ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
            ctaIcon: 'chevron-right',
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
          offset: {
            offsetX: -240,
            offsetY: 45,
          },
        },
      },
      {
        id: 'walkthrough-step-publish-job-3',
        position: 3,

        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['middle', 'right'],
            heading: this.i18n.translate('app.walkthrough.walkthroughs.publish_job.step_3.heading'),
            description: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_3.description',
            ),
            ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
            ctaIcon: 'chevron-right',
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
          offset: { offsetX: -24, offsetY: 0 },
        },
      },
      {
        id: 'walkthrough-step-publish-job-4',
        position: 4,
        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['left', 'bottom'],
            heading: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_4.heading_new',
            ),
            description: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_4.description_new',
            ),
            ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
            ctaIcon: 'chevron-right',
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' }],
          offset: { offsetX: -24, offsetY: -24 },
        },
      },
      {
        id: 'walkthrough-step-publish-job-6',
        position: 5,
        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['middle', 'bottom'],
            heading: this.i18n.translate('app.walkthrough.walkthroughs.publish_job.step_6.heading'),
            description: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_6.description',
            ),
            ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
            ctaIcon: 'chevron-right',
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' }],
          offset: { offsetX: -24, offsetY: -24 },
        },
      },
      {
        id: 'walkthrough-step-publish-job-5',
        position: 6,

        component: {
          definition: WalkthroughPostComponent,
          inputs: {
            heading: this.i18n.translate('app.walkthrough.walkthroughs.publish_job.step_5.heading'),
            description: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_5.description',
            ),
            ctaText: this.i18n.translate(
              'app.walkthrough.walkthroughs.publish_job.step_5.cta_text',
            ),
            helpArticleKey: 'freeJobBoards',
          },
        },
        renderOptions: {
          strategy: globalPositionStrategyFactory,
        },
        finishHandler: () => {
          this.finishHandler();
        },
      },
    ];
  }

  private finishHandler(): void {
    this.router
      .navigate([{ outlets: { 'job-editor': null } }])
      .then(() => this.router.navigate(['offers']));
  }
}
